/**
 * Scheda "Quanto ci resta" del progetto (26/10/2026).
 *
 * La domanda: di questo lavoro, quanto ci resta davvero? Lorenzo: "il goal è
 * capire quanto margine resta: le spese, l'IVA, le tasse (TUTTE), le provvigioni
 * agli agenti che ce li hanno portati". La forma: un conto che scende, riga per
 * riga, come uno scontrino (dal prezzo pagato dal cliente a quello che resta),
 * e accanto le tre cose da tenere aggiornate perché il conto sia vero: chi ha
 * portato il cliente, le fatture del progetto (la provvigione matura quando il
 * cliente paga) e, sotto, le spese.
 * I numeri li calcola il server (apps/api/src/modules/projects/conto.service.ts).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { fmtEur } from '../../lib/utils';
import { agentiLiteQuery, contoQuery, fattureClienteQuery, type ContoProgetto } from './projectsApi';
import './progetti.css';

const euro = (n: number) => `€ ${fmtEur(n)}`;
const pct = (n: number) => `${String(n).replace('.', ',')}%`;

const CATEGORIE: Array<[string, string]> = [
  ['ADV', 'Pubblicità (budget delle campagne)'],
  ['PROFESSIONISTI', 'Professionisti esterni'],
  ['FORN', 'Fornitori'],
  ['TOOLS', 'Software e strumenti'],
  ['INFRASTRUTTURA', 'Server e hosting'],
  ['SVILUPPO', 'Sviluppo'],
  ['DESIGN', 'Grafica e design'],
  ['PM', 'Gestione del progetto'],
  ['QA', 'Controlli e collaudi'],
  ['DOCENTI', 'Docenti'],
  ['RECRUITER', 'Selezione del personale'],
  ['ALTRO', 'Altro'],
];
const NOME_CATEGORIA = Object.fromEntries(CATEGORIE);

export function ContoTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const contoQ = useQuery(contoQuery(projectId));
  const agentiQ = useQuery(agentiLiteQuery);
  const c = contoQ.data;

  const aggiorna = (nuovo: ContoProgetto) => {
    qc.setQueryData(contoQuery(projectId).queryKey, nuovo);
    qc.invalidateQueries({ queryKey: ['projects', 'list'] });
    qc.invalidateQueries({ queryKey: ['projects', 'board'] });
    qc.invalidateQueries({ queryKey: ['agents'] });
  };
  const errore = (e: unknown) => toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.');

  const agente = useMutation({
    mutationFn: (b: { agentId: string | null; agentPct?: number | null }) => api.patch<ContoProgetto>(`/projects/${projectId}/agente`, b),
    onSuccess: (r) => { aggiorna(r); toast.ok('Agente aggiornato'); },
    onError: errore,
  });

  if (contoQ.isLoading || !c) return <p className="pg-vuoto">Faccio il conto del progetto…</p>;

  return (
    <div className="pc-grid">
      <div className="pc-main">
        {c.avvisi.length > 0 && (
          <div className="pc-avvisi">
            {c.avvisi.map((a) => <p key={a}><Icon name="help" size={12} /> {a}</p>)}
          </div>
        )}

        <section className="pc-scontrino" aria-label="Il conto del progetto">
          <Riga l="Il cliente paga" v={euro(c.totaleCliente)} />
          <Riga l={`di cui IVA ${pct(c.aliquote.iva)}, da versare allo Stato`} v={euro(c.iva)} sotto />
          <ValoreLavoro projectId={projectId} valore={c.imponibile} onSalvato={() => { qc.invalidateQueries({ queryKey: contoQuery(projectId).queryKey }); qc.invalidateQueries({ queryKey: ['project', projectId] }); qc.invalidateQueries({ queryKey: ['projects'] }); }} />
          <Riga l={`Spese del progetto${c.spese.righe.length ? ` (${c.spese.righe.length})` : ''}`} v={euro(c.spese.totale)} meno />
          <Riga
            l={c.provvigione.agente ? `Provvigione di ${c.provvigione.agente.nome}, ${pct(c.provvigione.pct)}` : 'Provvigione: nessun agente'}
            v={euro(c.provvigione.importo)}
            meno
          />
          <div className="pc-filo" />
          <Riga l="Utile del progetto" v={euro(c.utile)} forte />
          <Riga l={`IRES ${pct(c.aliquote.ires)} sull'utile`} v={euro(c.tasse.ires)} meno />
          <Riga l={`IRAP ${pct(c.aliquote.irap)} sull'utile`} v={euro(c.tasse.irap)} meno />
          <div className="pc-filo doppio" />
          <div className="pc-resta">
            <span className="l">Ci restano</span>
            <span className={`v${c.resta < 0 ? ' neg' : ''}`}>{euro(c.resta)}</span>
            <span className="p">{c.imponibile > 0 ? `${pct(c.restaPct)} del valore del lavoro` : ''}</span>
          </div>
          <p className="pc-nota">IRES e IRAP sono una stima sull'utile di questo progetto: la dichiarazione vera guarda tutta l'azienda.</p>
        </section>

        <Spese projectId={projectId} c={c} onConto={aggiorna} onErrore={errore} />
      </div>

      <aside className="pc-side">
        <section className="pc-box">
          <h3 className="pc-h3"><Icon name="handshake" size={14} /> Chi ci ha portato il cliente</h3>
          <select
            className="inp"
            aria-label="Agente"
            value={c.provvigione.agente?.id ?? ''}
            disabled={agente.isPending}
            onChange={(e) => agente.mutate({ agentId: e.target.value || null, agentPct: null })}
          >
            <option value="">Nessun agente</option>
            {(agentiQ.data ?? []).filter((a) => a.active || a.id === c.provvigione.agente?.id).map((a) => (
              <option key={a.id} value={a.id}>{a.name}, {pct(a.commissionPct)}</option>
            ))}
          </select>
          {c.provvigione.agente && (
            <>
              <PctProgetto c={c} disabled={agente.isPending} onSalva={(v) => agente.mutate({ agentId: c.provvigione.agente!.id, agentPct: v })} />
              <dl className="pc-kv">
                <div><dt>Provvigione sul lavoro</dt><dd>{euro(c.provvigione.importo)}</dd></div>
                <div><dt>Maturata, su {euro(c.fatture.incassato)} già incassati</dt><dd>{euro(c.provvigione.maturata)}</dd></div>
                <div><dt>Già pagata</dt><dd>{euro(c.provvigione.pagata)}</dd></div>
                <div className="forte"><dt>Da pagare adesso</dt><dd>{euro(c.provvigione.daPagare)}</dd></div>
              </dl>
              <Link className="pc-link" to={`/agenti?agente=${c.provvigione.agente.id}`}>Scheda di {c.provvigione.agente.nome} <Icon name="chevronR" size={11} /></Link>
            </>
          )}
        </section>

        <Fatture projectId={projectId} c={c} onConto={aggiorna} onErrore={errore} />
      </aside>
    </div>
  );
}

function Riga({ l, v, meno, forte, sotto }: { l: string; v: string; meno?: boolean; forte?: boolean; sotto?: boolean }) {
  return (
    <div className={`pc-riga${forte ? ' forte' : ''}${sotto ? ' sotto' : ''}`}>
      <span className="l">{meno && <span className="segno">−</span>}{l}</span>
      <span className="v">{v}</span>
    </div>
  );
}

/** Il valore del lavoro senza IVA, modificabile qui: prima non c'era nessun posto dove cambiarlo. */
function ValoreLavoro({ projectId, valore, onSalvato }: { projectId: string; valore: number; onSalvato: () => void }) {
  const [aperto, setAperto] = useState(false);
  const [testo, setTesto] = useState('');
  const numero = Number(testo.replace(/\./g, '').replace(',', '.'));
  const salva = useMutation({
    mutationFn: () => api.patch(`/projects/${projectId}`, { revenue: numero }),
    onSuccess: () => { setAperto(false); onSalvato(); toast.ok('Valore del lavoro aggiornato'); },
    onError: (e) => toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.'),
  });
  if (!aperto) {
    return (
      <div className="pc-riga forte">
        <span className="l">
          Valore del lavoro, senza IVA
          <button className="pc-modifica" onClick={() => { setTesto(valore ? String(valore).replace('.', ',') : ''); setAperto(true); }} title="Cambia il valore del lavoro" aria-label="Cambia il valore del lavoro"><Icon name="pencil" size={11} /></button>
        </span>
        <span className="v">{euro(valore)}</span>
      </div>
    );
  }
  return (
    <form className="pc-riga forte pc-valore" onSubmit={(e) => { e.preventDefault(); if (!Number.isNaN(numero) && numero >= 0) salva.mutate(); }}>
      <span className="l">Valore del lavoro, senza IVA</span>
      <span className="pc-valore-campo">
        <input className="inp" autoFocus inputMode="decimal" value={testo} onChange={(e) => setTesto(e.target.value)} aria-label="Valore del lavoro senza IVA" />
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAperto(false)}>Chiudi</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={Number.isNaN(numero) || numero < 0 || salva.isPending}>Salva</button>
      </span>
    </form>
  );
}

function PctProgetto({ c, disabled, onSalva }: { c: ContoProgetto; disabled: boolean; onSalva: (v: number | null) => void }) {
  const [valore, setValore] = useState(String(c.provvigione.pct).replace('.', ','));
  const numero = Number(valore.replace(',', '.'));
  const cambiato = !Number.isNaN(numero) && numero !== c.provvigione.pct;
  return (
    <div className="pc-pct">
      <label>
        <span>Percentuale su questo progetto</span>
        <span className="pc-pct-campo">
          <input className="inp" inputMode="decimal" value={valore} onChange={(e) => setValore(e.target.value)} aria-label="Percentuale della provvigione" />
          <span>%</span>
        </span>
      </label>
      {cambiato && <button className="btn btn-sm" disabled={disabled || numero < 0 || numero > 100} onClick={() => onSalva(numero)}>Salva</button>}
      {!c.provvigione.pctDaAgente && !cambiato && (
        <button className="btn btn-sm btn-ghost" disabled={disabled} onClick={() => onSalva(null)}>Usa quella dell'agente</button>
      )}
    </div>
  );
}

function Spese({ projectId, c, onConto, onErrore }: { projectId: string; c: ContoProgetto; onConto: (x: ContoProgetto) => void; onErrore: (e: unknown) => void }) {
  const [aperto, setAperto] = useState(false);
  const [f, setF] = useState({ categoria: 'ADV', descrizione: '', importo: '', data: '' });
  const importo = Number(f.importo.replace(/\./g, '').replace(',', '.'));
  const valida = f.descrizione.trim().length > 0 && f.importo.trim() !== '' && !Number.isNaN(importo) && importo >= 0;

  const aggiungi = useMutation({
    mutationFn: () => api.post<ContoProgetto>(`/projects/${projectId}/spese`, { categoria: f.categoria, descrizione: f.descrizione.trim(), importo, data: f.data || null }),
    onSuccess: (r) => { onConto(r); setF({ categoria: f.categoria, descrizione: '', importo: '', data: '' }); toast.ok('Spesa aggiunta'); },
    onError: onErrore,
  });
  const togli = useMutation({
    mutationFn: (id: string) => api.delete<ContoProgetto>(`/projects/spese/${id}`),
    onSuccess: (r) => { onConto(r); toast.ok('Spesa tolta'); },
    onError: onErrore,
  });

  return (
    <section className="pc-box pc-spese">
      <div className="pc-box-hd">
        <h3 className="pc-h3"><Icon name="receipt" size={14} /> Spese del progetto</h3>
        {!aperto && <button className="btn btn-sm" onClick={() => setAperto(true)}><Icon name="plus" size={12} /> Aggiungi una spesa</button>}
      </div>
      {c.spese.righe.length === 0 && !aperto && (
        <p className="pc-vuoto">Nessuna spesa. Metti qui quello che il lavoro ci costa: il budget delle campagne, i professionisti esterni, i software, gli stampati.</p>
      )}
      {c.spese.righe.length > 0 && (
        <div className="pc-spese-righe">
          {c.spese.righe.map((r) => (
            <div key={r.id} className="pc-spesa">
              <span className="cat">{NOME_CATEGORIA[r.categoria] ?? r.categoria}</span>
              <span className="ds">{r.descrizione}{r.fornitore ? ` · ${r.fornitore.nome}` : ''}</span>
              <span className="dt">{r.data ? new Date(r.data).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
              <span className="im">{euro(r.importo)}</span>
              <button className="pc-x" aria-label={`Togli la spesa ${r.descrizione}`} title="Togli questa spesa" disabled={togli.isPending} onClick={() => togli.mutate(r.id)}><Icon name="x" size={12} /></button>
            </div>
          ))}
        </div>
      )}
      {aperto && (
        <form className="pc-form" onSubmit={(e) => { e.preventDefault(); if (valida) aggiungi.mutate(); }}>
          <select className="inp" value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} aria-label="Tipo di spesa">
            {CATEGORIE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input className="inp" placeholder="Cosa è (es. budget Meta di novembre)" value={f.descrizione} onChange={(e) => setF({ ...f, descrizione: e.target.value })} aria-label="Descrizione" />
          <input className="inp" placeholder="Importo senza IVA" inputMode="decimal" value={f.importo} onChange={(e) => setF({ ...f, importo: e.target.value })} aria-label="Importo" />
          <input className="inp" type="date" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} aria-label="Data" />
          <div className="pc-form-az">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAperto(false)}>Chiudi</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={!valida || aggiungi.isPending}>Aggiungi</button>
          </div>
        </form>
      )}
    </section>
  );
}

function Fatture({ projectId, c, onConto, onErrore }: { projectId: string; c: ContoProgetto; onConto: (x: ContoProgetto) => void; onErrore: (e: unknown) => void }) {
  const qc = useQueryClient();
  const [aperto, setAperto] = useState(false);
  const q = useQuery({ ...fattureClienteQuery(projectId), enabled: true });
  const collegate = (q.data ?? []).filter((x) => x.collegata);
  const libere = (q.data ?? []).filter((x) => !x.collegata);
  const [scelte, setScelte] = useState<string[]>([]);

  const salva = useMutation({
    mutationFn: (b: { collega?: string[]; scollega?: string[] }) => api.post<ContoProgetto>(`/projects/${projectId}/fatture`, b),
    onSuccess: (r) => { onConto(r); setScelte([]); qc.invalidateQueries({ queryKey: fattureClienteQuery(projectId).queryKey }); toast.ok('Fatture del progetto aggiornate'); },
    onError: onErrore,
  });

  return (
    <section className="pc-box">
      <h3 className="pc-h3"><Icon name="invoice" size={14} /> Fatture del progetto</h3>
      <dl className="pc-kv">
        <div><dt>Fatturato</dt><dd>{euro(c.fatture.fatturato)}</dd></div>
        <div><dt>Incassato</dt><dd>{euro(c.fatture.incassato)}</dd></div>
        <div><dt>Ancora da fatturare</dt><dd>{euro(Math.max(0, c.imponibile - c.fatture.fatturato))}</dd></div>
      </dl>
      {collegate.length > 0 && (
        <div className="pc-fatture">
          {collegate.map((x) => (
            <div key={x.id} className="pc-fattura">
              <Link to={`/invoices/${x.id}`} className="n">{x.number}</Link>
              <span className="d">{x.issueDate ? new Date(x.issueDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: '2-digit' }) : ''}</span>
              <span className="i">{euro(x.subtotal)}</span>
              <button className="pc-x" title="Non è di questo progetto" aria-label={`Scollega la fattura ${x.number}`} disabled={salva.isPending} onClick={() => salva.mutate({ scollega: [x.id] })}><Icon name="x" size={11} /></button>
            </div>
          ))}
        </div>
      )}
      {libere.length > 0 && !aperto && (
        <button className="btn btn-sm pc-collega" onClick={() => setAperto(true)}><Icon name="link" size={12} /> Collega altre fatture del cliente ({libere.length})</button>
      )}
      {aperto && (
        <div className="pc-scegli">
          <p className="pc-vuoto">Spunta le fatture che fanno parte di questo lavoro (gli abbonamenti non c'entrano).</p>
          <div className="pc-scegli-elenco">
            {libere.map((x) => (
              <label key={x.id} className="pc-scegli-riga">
                <input type="checkbox" checked={scelte.includes(x.id)} onChange={(e) => setScelte(e.target.checked ? [...scelte, x.id] : scelte.filter((s) => s !== x.id))} />
                <span className="n">{x.number}</span>
                <span className="ds">{x.notes ?? ''}</span>
                <span className="i">{euro(x.subtotal)}</span>
              </label>
            ))}
          </div>
          <div className="pc-form-az">
            <button className="btn btn-ghost btn-sm" onClick={() => { setAperto(false); setScelte([]); }}>Chiudi</button>
            <button className="btn btn-primary btn-sm" disabled={!scelte.length || salva.isPending} onClick={() => salva.mutate({ collega: scelte })}>Collega {scelte.length || ''}</button>
          </div>
        </div>
      )}
    </section>
  );
}
